import { useCallback, useEffect, useRef, useState } from 'react';
import type { DependencyList } from 'react';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** Re-runs the loader, keeping any data already on screen. */
  reload: () => void;
  /** Optimistically replace the loaded value without a round trip. */
  setData: (value: T | null) => void;
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

/**
 * Runs an aborting async loader whenever `deps` change. Aborted requests never write state, so
 * fast filter typing cannot produce out-of-order results.
 */
export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: DependencyList,
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError(null);

    loaderRef
      .current(controller.signal)
      .then((result) => {
        if (!active) return;
        setData(result);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError(messageOf(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, reload, setData };
}

/** Debounced mirror of a rapidly changing value (search boxes). */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Tracks whether the component is still mounted; guards state writes after async work. */
export function useIsMounted(): () => boolean {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return useCallback(() => mounted.current, []);
}

/** Runs an action, tracking a pending flag and surfacing errors to the caller. */
export function useAction(): {
  pending: boolean;
  run: <T>(action: () => Promise<T>) => Promise<T | undefined>;
} {
  const [pending, setPending] = useState(false);
  const isMounted = useIsMounted();

  const run = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T | undefined> => {
      setPending(true);
      try {
        return await action();
      } finally {
        if (isMounted()) setPending(false);
      }
    },
    [isMounted],
  );

  return { pending, run };
}

/** Closes a drawer/modal on Escape and traps initial focus. */
export function useDismissable(open: boolean, onDismiss: () => void): void {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onDismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onDismiss]);
}
